'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { X } from 'lucide-react';
import { detectRegion, setConsent, getConsent, logConsentAction, type ConsentRegion } from '@/lib/consent';
import ConsentPreferenceCenter from './ConsentPreferenceCenter';

export default function CookieBanner() {
  const [region, setRegion] = useState<ConsentRegion>('none');
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    const detectedRegion = detectRegion();
    setRegion(detectedRegion);

    // Only show banner if no consent has been given yet
    const existingConsent = getConsent();
    if (!existingConsent && detectedRegion !== 'none') {
      setShowBanner(true);
    }
  }, []);

  const handleAcceptAll = () => {
    setConsent(
      {
        necessary: true,
        analytics: true,
        marketing: true,
        preferences: true,
      },
      region
    );
    logConsentAction('accept_all', region);
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    setConsent(
      {
        necessary: true,
        analytics: false,
        marketing: false,
        preferences: false,
      },
      region
    );
    logConsentAction('reject_all', region);
    setShowBanner(false);
  };

  const handleDismiss = () => {
    if (region === 'ccpa') {
      // CCPA: save with analytics=true, marketing=false, preferences=true
      setConsent(
        {
          necessary: true,
          analytics: true,
          marketing: false,
          preferences: true,
        },
        region
      );
      logConsentAction('dismiss', region);
    }
    setShowBanner(false);
  };

  if (!showBanner || region === 'none') {
    return null;
  }

  if (region === 'gdpr') {
    return (
      <>
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-lg">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col gap-4">
              {/* Header and Close */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">Sua Privacidade é Importante</h2>
                  <p className="text-sm text-gray-600 mb-3">
                    Usamos cookies e tecnologias similares para melhorar sua experiência. Você pode gerenciar suas
                    preferências ou aceitar todas as categorias. Leia nossa{' '}
                    <Link href="/privacy" className="text-blue-600 hover:underline">
                      Política de Privacidade
                    </Link>{' '}
                    e{' '}
                    <Link href="/cookies" className="text-blue-600 hover:underline">
                      Política de Cookies
                    </Link>
                    .
                  </p>
                </div>
                <button
                  onClick={() => setShowBanner(false)}
                  className="ml-4 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Fechar banner de cookies"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-end">
                <button
                  onClick={handleRejectAll}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Rejeitar Tudo
                </button>
                <button
                  onClick={() => setShowPreferences(true)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Gerenciar Preferências
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                >
                  Aceitar Tudo
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Preference Center Modal */}
        {showPreferences && (
          <ConsentPreferenceCenter
            region={region}
            onClose={() => setShowPreferences(false)}
            onSave={() => {
              setShowBanner(false);
              setShowPreferences(false);
            }}
          />
        )}
      </>
    );
  }

  if (region === 'ccpa') {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-700">
              Usamos cookies para melhorar sua experiência. Você pode{' '}
              <Link href="/privacy#do-not-sell" className="text-blue-600 hover:underline font-medium">
                não permitir a venda de seus dados pessoais
              </Link>
              .
            </p>
            <button
              onClick={handleDismiss}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors whitespace-nowrap"
            >
              Entendi
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
